import { useRef, useEffect, useState, useMemo } from "react";
import Map, { Source, Layer } from "react-map-gl/maplibre";
import maplibregl from "maplibre-gl";
import type { MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import omskRegion from "../../geo/omsk-region.geo.json";
import omskDistricts from "../../geo/omsk-districts.geo.json";
import { useCompaniesStore, useFilterStore, useOrganizationsStore } from "../../store";
import { CompanyDTO, CompanyOptimizedDTO, EducationalOrganizationsDTO, EducationalOrganizationsOptimizedDTO, OrganizationType } from "../../types";
import { MapContent } from "./map-content";
import * as mapUtils from "../../utils/map-utils";
import { ENTITY_TYPE, HoveredComp, HoveredOrg, SelectedCluster, SelectedComp, SelectedOrg } from "../../types/marker-types";
import { isOrganizationType } from "../../utils/organization.utils";

export default function OrganizationMap({ 
    overlayMode, 
    rmrIsOpen,
    setRmrIsOpen,
    rmrIsVisible,
    setRmrIsVisible,
    rmrShouldNavigate,
    setRmrShouldNavigate
}: { 
    overlayMode: "expanded" | "collapsed",
    rmrIsOpen: boolean;
    setRmrIsOpen: (value: boolean) => void;
    rmrIsVisible: boolean;
    setRmrIsVisible: (value: boolean) => void;
    rmrShouldNavigate: boolean;
    setRmrShouldNavigate: (value: boolean) => void;
}) {
    const mapRef = useRef<MapRef>(null);
    const firstLoad = useRef(true);

    const [organizations, setOrganizations] = useState<EducationalOrganizationsOptimizedDTO[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<SelectedOrg | null>(null);
    const [hoveredOrg, setHoveredOrg] = useState<HoveredOrg | null>(null);
    const [companies, setCompanies] = useState<CompanyOptimizedDTO[]>([]);
    const [selectedComp, setSelectedComp] = useState<SelectedComp | null>(null);
    const [hoveredComp, setHoveredComp] = useState<HoveredComp | null>(null);
    const [selectedCluster, setSelectedCluster] = useState<SelectedCluster | null>(null);
    const [maxBounds, setMaxBounds] = useState<[number, number, number, number] | undefined>();

    const {filter, category, results} = useFilterStore(s => s);
    const getOrganizations = useOrganizationsStore(s => s.getOrganizationsByIds);
    const allOrganizations = useOrganizationsStore(s => s.organizations);
    const { colleges, universities } = useMemo(() => {
        let colleges = [];
        let universities = [];

        for(const org of allOrganizations) {
            if(org.type === OrganizationType.SPO) colleges.push(org);
            else universities.push(org);
        }

        return {
            colleges,
            universities
        };
    }, [allOrganizations]);
    const getCompanies = useCompaniesStore(s => s.getCompaniesByIds);
    const allCompanies = useCompaniesStore(s => s.companies);

    const regionBounds = useMemo(() => mapUtils.getRegionBounds(omskRegion as any), []);
    const districtLabels = useMemo(() => mapUtils.getDistrictLabels(omskDistricts as any), []);
    const omskSolidFeature = useMemo(() => mapUtils.getOmskSolidFeature(omskDistricts as any, "Омский"), []);
    const districtsWithoutOmsk = useMemo(() => mapUtils.getDistrictsWithout(omskDistricts as any, "Омский"), []);

    useEffect(() => {
        if(overlayMode === "expanded") {
            setSelectedCluster(null);
            setSelectedOrg(null);
            setSelectedComp(null);
            setRmrIsOpen(false);
        }
    }, [overlayMode]);

    useEffect(() => {
        if(rmrIsOpen) {
            setSelectedCluster(null);
            setSelectedOrg(null);
            setSelectedComp(null);
        }
    }, [rmrIsOpen]);

    useEffect(() => {
        if(rmrIsOpen || rmrIsVisible) {
            setRmrIsVisible(false);
            setRmrIsOpen(false);
        }

        if(!results) {
            if(category === null) setOrganizations(allOrganizations);
            else if(category === ENTITY_TYPE.SPO) setOrganizations(colleges);
            else if(category === ENTITY_TYPE.UNIVERSITY) setOrganizations(universities);
            else setOrganizations([]);

            if(category === ENTITY_TYPE.COMPANY || category === null) setCompanies(allCompanies);
            else setCompanies([]);

            return;
        }

        if(isOrganizationType(category) || category === null) {
            const orgs = getOrganizations(results.organizationIds);
            if(orgs) setOrganizations(orgs);
        } else setOrganizations([]);

        if(category === ENTITY_TYPE.COMPANY || category === null) {
            const comps = getCompanies(results.companyIds);
            if(comps) setCompanies(comps);
        } else setCompanies([]);
    }, [filter, category, results, allCompanies, allOrganizations, getOrganizations, getCompanies]);

    useEffect(() => {
        if(!mapRef.current) return;

        if(!organizations.length && !companies.length) {
            mapRef.current.fitBounds(regionBounds, {
                padding: { top: 120, bottom: 40, left: 40, right: 40 },
                duration: 800,
            });

            return;
        }

        if(firstLoad.current) {
            firstLoad.current = false;

            if(!organizations.length && !companies.length) return;
        }

        const bounds = new maplibregl.LngLatBounds();
        [...organizations, ...companies].forEach(item => bounds.extend([item.longitude, item.latitude]));

        const searchEl = document.querySelector(".search-bar");
        const topPadding = searchEl?.clientHeight ?? 44;

        mapRef.current.fitBounds(bounds, {
            padding: {
                top: topPadding + 55,
                bottom: 80,
                left: 80,
                right: 80,
            },
            duration: 800,
            maxZoom: 15,
        });

        mapRef.current.once("moveend", () => {
            if(organizations.length === 1 && companies.length === 0) {
                const org = organizations[0];

                setSelectedOrg({
                    ...org,
                    longitude: org.longitude,
                    latitude: org.latitude,
                });

                return;
            }

            if(companies.length === 1 && organizations.length === 0) {
                const comp = companies[0];

                setSelectedComp({
                    ...comp,
                    longitude: comp.longitude,
                    latitude: comp.latitude,
                });
            }
        });
    }, [organizations, companies, regionBounds]);

    useEffect(() => {
        const updateBounds = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;

            const { horizontal, vertical } = mapUtils.getFactorsFromAspectRatio(width, height);

            const extended = mapUtils.extendBounds(
                regionBounds,
                horizontal,
                vertical
            );

            setMaxBounds(extended);
        };

        updateBounds();

        window.addEventListener("resize", updateBounds);

        return () => window.removeEventListener("resize", updateBounds);
    }, [regionBounds]);

    return (
        <div style={{ width: "100vw", height: "100vh" }}>
            <Map
                ref={mapRef}
                mapStyle="https://api.maptiler.com/maps/019c534c-988c-7404-a220-ae22d7009935/style.json?key=dwF8ODgfG11iIfBCQFra"
                renderWorldCopies={false}
                maxBounds={maxBounds}
                initialViewState={{
                    bounds: regionBounds,
                    fitBoundsOptions: { padding: 0 }
                }}
                attributionControl={false}
                maxZoom={18}
                onClick={() => { 
                    setSelectedOrg(null);
                    setSelectedComp(null);
                    setSelectedCluster(null);
                    setRmrIsOpen(false);
                }}
                onZoom={() => { 
                    setSelectedOrg(null); setHoveredOrg(null); 
                    setSelectedComp(null); setHoveredComp(null); 
                    setSelectedCluster(null);
                    if(!rmrShouldNavigate) setRmrIsOpen(false);
                }}
            >
                {/* Маска */}
                <Source
                    id="mask"
                    type="geojson"
                    data={mapUtils.getMaskGeoJSON(omskRegion)}
                >
                    <Layer
                        id="mask-fill"
                        type="fill"
                        paint={{
                            "fill-color": "#BED5F5",
                        }}
                    />
                </Source>

                {/* Районы без Омска */}
                <Source
                    id="districts-fill-source"
                    type="geojson"
                    data={districtsWithoutOmsk}
                >
                    <Layer
                        id="districts-fill"
                        type="fill"
                        paint={{
                            "fill-color": [
                                "step",
                                ["get", "level"],
                                "#E9F1FB",
                                1,
                                "#D5DDE5",
                                2,
                                "#ADC3D9",
                                3,
                                "#4D85D6",
                            ],
                            "fill-opacity": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                8,
                                1,
                                9,
                                0.6,
                                11,
                                0.2,
                            ],
                        }}
                    />
                </Source>

                {/* Линии районов */}
                <Source id="districts-line-source" type="geojson" data={omskDistricts}>
                    <Layer
                        id="districts-line"
                        type="line"
                        paint={{
                            "line-color": "grey",
                            "line-width": 2,
                            "line-opacity": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                8,
                                1,
                                9,
                                0.6,
                                11,
                                0.2,
                            ],
                        }}
                    />
                </Source>

                {/* Омский район */}
                {omskSolidFeature && (
                    <Source
                        id="omsk-district-solid"
                        type="geojson"
                        data={omskSolidFeature}
                    >
                        <Layer
                            id="omsk-district-solid-fill"
                            type="fill"
                            paint={{
                                "fill-color": "#4D85D6",
                                "fill-opacity": [
                                    "interpolate",
                                    ["linear"],
                                    ["zoom"],
                                    8,
                                    1,
                                    9,
                                    0.6,
                                    10,
                                    0.2,
                                ],
                            }}
                        />
                    </Source>
                )}

                {/* Метки районов */}
                <Source id="district-labels" type="geojson" data={districtLabels}>
                    <Layer
                        id="districts-labels"
                        type="symbol"
                        maxzoom={13}
                        layout={{
                            "text-field": ["get", "district"],
                            "text-size": [
                                "interpolate",
                                ["linear"],
                                ["zoom"],
                                5.5, 12,
                                5.8, 15,
                                7, 12,
                            ],
                        }}
                        paint={{
                            "text-halo-color": "#ffffff",
                            "text-color": "#000000",
                            "text-halo-width": 1,
                        }}
                    />
                </Source>

                {/* Кластеры и Popup */}
                <MapContent
                    rmrIsOpen={rmrIsOpen}
                    setRmrIsOpen={setRmrIsOpen}
                    rmrShouldNavigate={rmrShouldNavigate}
                    setRmrShouldNavigate={setRmrShouldNavigate}
                    rmrIsVisible={rmrIsVisible}
                    setRmrIsVisible={setRmrIsVisible}

                    organizations={organizations}
                    selectedOrg={selectedOrg}
                    setSelectedOrg={setSelectedOrg}
                    setHoveredOrg={setHoveredOrg}
                    hoveredOrg={hoveredOrg}
                    
                    companies={companies}
                    selectedComp={selectedComp}
                    setSelectedComp={setSelectedComp}
                    setHoveredComp={setHoveredComp}
                    hoveredComp={hoveredComp}
                    
                    selectedCluster={selectedCluster}
                    setSelectedCluster={setSelectedCluster}
                    mapRef={mapRef}
                />
            </Map>
        </div>
    );
}
